import { RelayPageDataTable } from "@constellation-academy/epic-ui";
import { useState } from "react";
import { usePaginationFragment } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import type { personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesFragment$key } from "@relay/personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesFragment.graphql";
import type { personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesListFragment$key } from "@relay/personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesListFragment.graphql";
import type { personalizedNotificationTemplatesTable_Refetch } from "@relay/personalizedNotificationTemplatesTable_Refetch.graphql";
import {
	QUERY_FRAGMENT,
	PERSONALIZED_NOTIFICATION_TEMPLATE_FRAGMENT,
} from "./personalized-notification-templates-table.graphql";
import { buttonWrapperClass } from "./personalized-notification-templates-table.styles";
import type { PersonalizedNotificationTemplatesTableProps } from "./personalized-notification-templates-table.types";
import { IdDisplayField } from "../../../../components/id-display-field";
import { CreatePersonalizedNotificationTemplateButton } from "../create-personalized-notification-template-button";
import { DeletePersonalizedNotificationTemplateButton } from "../delete-personalized-notification-template-button";
import { EditPersonalizedNotificationsTemplateButton } from "../edit-personalized-notifications-template-button";

export const PersonalizedNotificationTemplatesTable = ({
	personalizedNotificationTemplatesFragmentRef,
}: PersonalizedNotificationTemplatesTableProps) => {
	const { t } = useTranslation("common");
	const [connectionId, setConnectionId] = useState<string>("");

	const { data, refetch } = usePaginationFragment<
		personalizedNotificationTemplatesTable_Refetch,
		personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesListFragment$key
	>(QUERY_FRAGMENT, personalizedNotificationTemplatesFragmentRef);

	const handleOnConnectionIdChange = (id?: string | null) => {
		setConnectionId(id ?? "");
	};

	const handleRefetch = () => {
		refetch({}, { fetchPolicy: "network-only" });
	};

	return (
		<>
			<CreatePersonalizedNotificationTemplateButton
				connectionId={connectionId}
				availableTemplatesFragmentRef={data}
			/>
			<RelayPageDataTable<
				personalizedNotificationTemplatesTable_Refetch,
				personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesListFragment$key,
				personalizedNotificationTemplatesTable_PersonalizedNotificationTemplatesFragment$key
			>
				queryFragment={QUERY_FRAGMENT}
				parentFragmentRef={personalizedNotificationTemplatesFragmentRef}
				nodeInlineFragment={PERSONALIZED_NOTIFICATION_TEMPLATE_FRAGMENT}
				extractConnection={(data) =>
					data.Admin.Notification.GetPersonalizedNotificationTemplates
				}
				onConnectionIdChange={handleOnConnectionIdChange}
				rowsCount={10}
				emptyMessage={t("personalizedNotificationTemplates.noTemplates")}
			>
				{(Column) => (
					<>
						<Column
							header={t("personalizedNotificationTemplates.templateKey")}
							style={{ width: "14%" }}
							body={(item) => (
								<IdDisplayField
									id={item.template.key}
									label={t("personalizedNotificationTemplates.templateKey")}
								/>
							)}
						/>
						<Column header={t("general.title")} field="template.title" />
						<Column
							header={t("table.actions")}
							style={{ width: "10%" }}
							body={(item) => (
								<div className={buttonWrapperClass}>
									<EditPersonalizedNotificationsTemplateButton
										personalizedNotificationTemplateId={item.id}
									/>
									<DeletePersonalizedNotificationTemplateButton
										personalizedNotificationTemplateId={item.id}
										connectionId={connectionId}
										onDelete={handleRefetch}
									/>
								</div>
							)}
						/>
					</>
				)}
			</RelayPageDataTable>
		</>
	);
};
